import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import type { ExpenseAttachment } from "../api/types";

const ICON_FOR_TYPE = (ct: string) => (ct.startsWith("image/") ? "🖼️" : ct === "application/pdf" ? "📄" : "📎");

export function AttachmentList({
  requestId,
  lineId,
  attachments,
  canEdit,
}: {
  requestId: string;
  lineId: string | null;
  attachments: ExpenseAttachment[];
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const upload = useMutation({
    mutationFn: (file: File) => api.expenses.uploadAttachment(requestId, file, lineId ?? undefined),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["claim", requestId] });
      if (res.duplicate_of) {
        setError(`⚠ Looks like a duplicate of an attachment uploaded ${new Date(res.duplicate_of.uploaded_at).toLocaleDateString()} — double-check before submitting.`);
      } else {
        setError(null);
      }
    },
    onError: (e) => setError((e as Error).message),
  });

  const remove = useMutation({
    mutationFn: (aid: string) => api.expenses.deleteAttachment(requestId, aid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["claim", requestId] }),
  });

  const open = async (aid: string) => {
    try {
      const r = await api.expenses.attachmentDownloadUrl(requestId, aid);
      window.open(r.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setError(null);
    upload.mutate(f);
    e.target.value = ""; // allow same-file re-select
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {attachments.map((a) => (
        <span
          key={a.id}
          className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full max-w-[18rem] ${
            a.is_declaration
              ? "bg-amber-50 text-amber-800 border border-amber-200"
              : "bg-liaise-navy-50 text-liaise-navy"
          }`}
        >
          <button onClick={() => open(a.id)} className="flex items-center gap-1.5 hover:underline truncate">
            <span>{a.is_declaration ? "📝" : ICON_FOR_TYPE(a.content_type)}</span>
            <span className="truncate">{a.file_name}</span>
          </button>
          {canEdit && (
            <button
              onClick={() => remove.mutate(a.id)}
              className="text-slate-400 hover:text-red-600 ml-1"
              title="Remove"
            >
              ×
            </button>
          )}
        </span>
      ))}
      {canEdit && (
        <>
          <input
            ref={fileInput}
            type="file"
            accept="image/*,application/pdf"
            onChange={onPick}
            className="hidden"
          />
          <button
            onClick={() => fileInput.current?.click()}
            disabled={upload.isPending}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-liaise-navy bg-liaise-navy-50 border border-liaise-navy-200 rounded-full px-3 py-1 hover:bg-liaise-navy-200 disabled:opacity-50 transition"
          >
            {upload.isPending ? "Uploading…" : attachments.length === 0 ? "📎 Attach receipt" : "📎 Add another"}
          </button>
        </>
      )}
      {error && <span className="text-xs text-red-600">{error}</span>}
    </div>
  );
}
